import '../i18n/autoTranslate';
import { useState, useRef } from "react";

import {API_BASE_URL, getAuthHeaders} from "../../config/api";

const SAMPLE_TRANSCRIPTS = [
  {
    label: "Strong Candidate",
    text: "I led a cross-functional team of eight engineers and delivered a platform migration that improved system performance by 40%. I developed the architecture, coordinated with stakeholders, and successfully launched ahead of schedule. My approach was to identify bottlenecks early, build iterative solutions, and collaborate closely with the product team to ensure alignment.",
    duration: 28,
    response_delay: 1.2,
  },
  {
    label: "Filler-Heavy",
    text: "Um, so basically I kind of, you know, like worked on this project and I guess we were like trying to improve things and um it was sort of successful I think, yeah? Like we did some stuff and um I don't know it turned out okay I suppose.",
    duration: 20,
    response_delay: 4.5,
  },
  {
    label: "Uncertain / Hedging",
    text: "I'm not really sure how to describe it. I think I maybe contributed to the project? I suppose I was involved in some of the analysis but honestly I don't know if it made a difference. Hopefully it was useful, if that makes sense.",
    duration: 15,
    response_delay: 8.0,
  },
];

const COLORS = {
  bg: "#0a0a0f",
  surface: "#12121a",
  card: "#1a1a26",
  border: "#252535",
  accent: "#7c6af7",
  accentGlow: "#7c6af740",
  green: "#22d3a0",
  yellow: "#f5c842",
  red: "#f05a5a",
  text: "#e8e8f0",
  muted: "#6b6b8a",
  faint: "#2a2a3a",
};

function scoreColor(score) {
  if (score === null || score === undefined) return COLORS.muted;
  if (score >= 85) return COLORS.green;
  if (score >= 70) return "#7c6af7";
  if (score >= 55) return COLORS.yellow;
  if (score >= 40) return "#f0944a";
  return COLORS.red;
}

function Arc({ score, size = 80 }) {
  if (score === null || score === undefined) return (
    <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.muted, fontSize: 12 }}>N/A</div>
  );
  const r = (size - 12) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference * (1 - score / 100);
  const color = scoreColor(score);

  return (
    <svg width={size} height={size} style={{ display: "block", filter: `drop-shadow(0 0 6px ${color}60)` }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={COLORS.faint} strokeWidth={6} />
      <circle
        cx={cx} cy={cy} r={r} fill="none"
        stroke={color} strokeWidth={6}
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
        strokeLinecap="round"
        transform={`rotate(-90 ${cx} ${cy})`}
        style={{ transition: "stroke-dashoffset 0.7s cubic-bezier(.4,0,.2,1)" }}
      />
      <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="middle"
        fill={color} fontSize={size < 70 ? 13 : 15} fontWeight="700" fontFamily="'DM Mono', monospace">
        {Math.round(score)}
      </text>
    </svg>
  );
}

function Badge({ label }) {
  const colors = {
    Excellent: { bg: "#0d2e22", text: COLORS.green, border: "#1a5c42" },
    Good: { bg: "#1a1740", text: "#a89cf7", border: "#3d3680" },
    Average: { bg: "#2e2a0a", text: COLORS.yellow, border: "#5c520f" },
    "Below Average": { bg: "#2e1a0a", text: "#f0944a", border: "#5c360f" },
    Poor: { bg: "#2e0a0a", text: COLORS.red, border: "#5c1515" },
    "N/A": { bg: COLORS.faint, text: COLORS.muted, border: COLORS.border },
  };
  const c = colors[label] || colors["N/A"];
  return (
    <span style={{
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
      borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600,
      letterSpacing: "0.04em", textTransform: "uppercase", fontFamily: "'DM Mono', monospace",
    }}>{label}</span>
  );
}

function FillerBar({ word, count, maxCount }) {
  const pct = maxCount ? (count / maxCount) * 100 : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
      <span style={{
        fontFamily: "'DM Mono', monospace", fontSize: 12, color: COLORS.accent,
        minWidth: 110, maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>{word}</span>
      <div style={{ flex: 1, height: 6, background: COLORS.faint, borderRadius: 3, overflow: "hidden" }}>
        <div style={{
          width: `${pct}%`, height: "100%", borderRadius: 3,
          background: `linear-gradient(90deg, ${COLORS.accent}, ${COLORS.green})`,
          transition: "width 0.5s cubic-bezier(.4,0,.2,1)",
        }} />
      </div>
      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: COLORS.text, minWidth: 18, textAlign: "right" }}>{count}</span>
    </div>
  );
}

function ScoreCard({ label, score, grade }) {
  return (
    <div style={{
      background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 12,
      padding: "18px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
      transition: "border-color 0.2s",
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.accent}
      onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}
    >
      <Arc score={score} size={72} />
      <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: COLORS.muted, textTransform: "uppercase", letterSpacing: "0.07em", textAlign: "center" }}>{label}</div>
      <Badge label={grade || "N/A"} />
    </div>
  );
}

function MetaPill({ label, value }) {
  return (
    <div style={{
      background: COLORS.faint, border: `1px solid ${COLORS.border}`,
      borderRadius: 8, padding: "6px 14px", display: "flex", flexDirection: "column", alignItems: "center",
    }}>
      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>
      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 14, color: COLORS.text, fontWeight: 600, marginTop: 2 }}>{value ?? "—"}</span>
    </div>
  );
}

function ResultPanel({ result, error }) {
  if (error) return (
    <div style={{
      background: "#2e0a0a", border: `1px solid #5c1515`, borderRadius: 12,
      padding: 24, color: COLORS.red, fontFamily: "'DM Mono', monospace", fontSize: 13,
    }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>⚠ Error</div>
      <div>{error}</div>
    </div>
  );

  if (!result) return null;

  const { scores, grades, filler_words, meta } = result;
  const topFillers = filler_words?.top_fillers || [];
  const maxCount = topFillers.length ? Math.max(...topFillers.map(f => f.count)) : 1;

  const subScores = [
    { label: "Clarity", key: "clarity" },
    { label: "Professionalism", key: "professionalism" },
    { label: "Confidence", key: "confidence" },
    { label: "Pacing", key: "pacing" },
    { label: "Response Time", key: "response_time" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Overall */}
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.card} 0%, #1e1b3a 100%)`,
        border: `1px solid ${COLORS.accent}40`, borderRadius: 14,
        padding: "24px 28px", display: "flex", alignItems: "center", gap: 24,
      }}>
        <Arc score={scores.overall} size={96} />
        <div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: COLORS.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>Overall Score</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: scoreColor(scores.overall), fontFamily: "'DM Mono', monospace", lineHeight: 1.1 }}>
            {Math.round(scores.overall)}
            <span style={{ fontSize: 16, color: COLORS.muted, fontWeight: 400 }}>/100</span>
          </div>
          <div style={{ marginTop: 6 }}><Badge label={grades.overall} /></div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <MetaPill label="Words" value={meta.word_count} />
          <MetaPill label="WPM" value={meta.wpm} />
          <MetaPill label="Duration" value={meta.duration_seconds ? `${meta.duration_seconds}s` : null} />
          <MetaPill label="Delay" value={meta.response_delay_seconds != null ? `${meta.response_delay_seconds}s` : null} />
          <MetaPill label="Computed" value={`${meta.processing_ms}ms`} />
        </div>
      </div>

      {/* Sub-score grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
        {subScores.map(({ label, key }) => (
          <ScoreCard key={key} label={label} score={scores[key]} grade={grades[key]} />
        ))}
      </div>

      {/* Filler Words */}
      <div style={{
        background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 20,
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24,
      }}>
        <div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: COLORS.muted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 14 }}>Filler Words</div>
          <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 22, fontWeight: 800, color: COLORS.text }}>{filler_words.total_count}</div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: COLORS.muted, textTransform: "uppercase" }}>Total Fillers</div>
            </div>
            <div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 22, fontWeight: 800, color: scoreColor(100 - filler_words.ratio * 400) }}>
                {(filler_words.ratio * 100).toFixed(1)}%
              </div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: COLORS.muted, textTransform: "uppercase" }}>Ratio</div>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 4 }}>
              <Badge label={filler_words.severity} />
            </div>
          </div>
        </div>
        <div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: COLORS.muted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12 }}>Top Fillers</div>
          {topFillers.length === 0
            ? <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: COLORS.muted }}>No fillers detected 🎉</div>
            : topFillers.slice(0, 8).map(f => (
              <FillerBar key={f.word} word={f.word} count={f.count} maxCount={maxCount} />
            ))
          }
        </div>
      </div>

      {/* ML features */}
      <div style={{ display: "flex", gap: 10 }}>
        {[
          { label: "TextBlob", active: meta.ml_features?.textblob },
          { label: "spaCy", active: meta.ml_features?.spacy },
        ].map(({ label, active }) => (
          <div key={label} style={{
            fontFamily: "'DM Mono', monospace", fontSize: 11, padding: "4px 12px",
            borderRadius: 20, border: `1px solid ${active ? COLORS.green + "60" : COLORS.border}`,
            color: active ? COLORS.green : COLORS.muted,
            background: active ? "#0d2e2220" : "transparent",
          }}>
            {active ? "✓" : "✗"} {label}
          </div>
        ))}
      </div>
    </div>
  );
}

function RawJSON({ data }) {
  const [open, setOpen] = useState(false);
  if (!data) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        background: "none", border: `1px solid ${COLORS.border}`, color: COLORS.muted,
        fontFamily: "'DM Mono', monospace", fontSize: 11, padding: "4px 12px", borderRadius: 6,
        cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.06em",
      }}>
        {open ? "▲ Hide" : "▼ Raw JSON"}
      </button>
      {open && (
        <pre style={{
          background: "#080810", border: `1px solid ${COLORS.border}`, borderRadius: 10,
          padding: 16, marginTop: 10, fontSize: 11, color: "#8888b8",
          fontFamily: "'DM Mono', monospace", overflowX: "auto", maxHeight: 400, overflowY: "auto",
        }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}

export default function TranscriptAnalyzerTest() {
  const [transcript, setTranscript] = useState(SAMPLE_TRANSCRIPTS[0].text);
  const [duration, setDuration] = useState(String(SAMPLE_TRANSCRIPTS[0].duration));
  const [delay, setDelay] = useState(String(SAMPLE_TRANSCRIPTS[0].response_delay));
  const [loading, setLoading] = useState(false);
  const [loadingFiller, setLoadingFiller] = useState(false);
  const [loadingBatch, setLoadingBatch] = useState(false);
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [fillerResult, setFillerResult] = useState(null);
  const [fillerError, setFillerError] = useState(null);
  const [batchResult, setBatchResult] = useState(null);
  const [batchError, setBatchError] = useState(null);
  const [healthResult, setHealthResult] = useState(null);
  const [activeTab, setActiveTab] = useState("analyze");

  async function callAPI(url, body) {
    const res = await fetch(`${API_BASE_URL}${url}`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
    return json;
  }

  async function handleAnalyze() {
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const body = {
        transcript,
        ...(duration ? { duration: parseFloat(duration) } : {}),
        ...(delay ? { response_delay: parseFloat(delay) } : {}),
      };
      const data = await callAPI("/api/metrics/analyze", body);
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleFillers() {
    setLoadingFiller(true);
    setFillerResult(null);
    setFillerError(null);
    try {
      const data = await callAPI("/api/metrics/fillers", { transcript });
      setFillerResult(data);
    } catch (e) {
      setFillerError(e.message);
    } finally {
      setLoadingFiller(false);
    }
  }

  async function handleBatch() {
    setLoadingBatch(true);
    setBatchResult(null);
    setBatchError(null);
  
    try {
      const payload = {
        items: SAMPLE_TRANSCRIPTS.map((s) => ({
          label: s.label,
          transcript: s.text,
          duration: s.duration,
          response_delay: s.response_delay,
        })),
      };
  
      const res = await fetch(`${API_BASE_URL}/api/metrics/analyze/batch`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
  
      // safer parsing
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
  
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  
      setBatchResult(data);
    } catch (e) {
      setBatchError(e?.message || "Batch request failed");
    } finally {
      setLoadingBatch(false);
    }
  }

  async function handleHealth() {
    setLoadingHealth(true);
    setHealthResult(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/metrics/health`,{headers: getAuthHeaders()});
      const data = await res.json();
      setHealthResult(data);
    } catch (e) {
      setHealthResult({ error: e.message });
    } finally {
      setLoadingHealth(false);
    }
  }

  function loadSample(s) {
    setTranscript(s.text);
    setDuration(String(s.duration));
    setDelay(String(s.response_delay));
  }

  const tabs = [
    { id: "analyze", label: "POST /analyze" },
    { id: "fillers", label: "POST /fillers" },
    { id: "batch", label: "POST /batch" },
    { id: "health", label: "GET /health" },
  ];

  const inputStyle = {
    background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8,
    color: COLORS.text, fontFamily: "'DM Mono', monospace", fontSize: 13, padding: "8px 12px",
    outline: "none", width: "100%", boxSizing: "border-box",
    transition: "border-color 0.2s",
  };

  const btnStyle = (accent = false, disabled = false) => ({
    background: accent
      ? disabled ? COLORS.faint : `linear-gradient(135deg, ${COLORS.accent} 0%, #5e4fcf 100%)`
      : COLORS.faint,
    border: `1px solid ${accent ? COLORS.accent : COLORS.border}`,
    color: disabled ? COLORS.muted : (accent ? "#fff" : COLORS.text),
    borderRadius: 8, padding: "9px 20px",
    fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer", textTransform: "uppercase",
    letterSpacing: "0.07em", opacity: disabled ? 0.5 : 1,
    boxShadow: accent && !disabled ? `0 4px 20px ${COLORS.accentGlow}` : "none",
    transition: "all 0.15s",
  });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:ital,wght@0,300;0,400;0,500;1,300&family=Syne:wght@400;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: ${COLORS.surface}; }
        ::-webkit-scrollbar-thumb { background: ${COLORS.border}; border-radius: 3px; }
        textarea:focus, input:focus { border-color: ${COLORS.accent} !important; }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
      <div style={{ background: COLORS.bg, minHeight: "100vh", padding: "32px 24px", fontFamily: "'DM Mono', monospace" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>

          {/* Header */}
          <div style={{ marginBottom: 32, animation: "fadeUp 0.4s ease" }}>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 10,
              background: COLORS.accentGlow, border: `1px solid ${COLORS.accent}40`,
              borderRadius: 8, padding: "4px 14px", marginBottom: 12,
            }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: COLORS.green, animation: "pulse 2s infinite" }} />
              <span style={{ fontSize: 11, color: COLORS.accent, textTransform: "uppercase", letterSpacing: "0.1em" }}>API Test Suite</span>
            </div>
            <h1 style={{
              fontFamily: "'Syne', sans-serif", fontSize: 28, fontWeight: 800,
              color: COLORS.text, letterSpacing: "-0.02em", lineHeight: 1,
            }}>
              Transcript <span style={{ color: COLORS.accent }}>Analyzer</span>
            </h1>
            <p style={{ color: COLORS.muted, fontSize: 12, marginTop: 6 }}>
              End-to-end test interface for Flask NLP API at <span style={{ color: COLORS.accent }}>{API_BASE_URL}</span>
            </p>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: `1px solid ${COLORS.border}`, paddingBottom: 0 }}>
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                background: activeTab === t.id ? COLORS.card : "none",
                border: `1px solid ${activeTab === t.id ? COLORS.accent : "transparent"}`,
                borderBottom: activeTab === t.id ? `1px solid ${COLORS.card}` : "1px solid transparent",
                marginBottom: -1, borderRadius: "8px 8px 0 0",
                color: activeTab === t.id ? COLORS.text : COLORS.muted,
                fontFamily: "'DM Mono', monospace", fontSize: 11, padding: "8px 16px",
                cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.06em",
                fontWeight: activeTab === t.id ? 600 : 400,
              }}>
                {t.label}
              </button>
            ))}
          </div>

          {/* ── ANALYZE TAB ── */}
          {activeTab === "analyze" && (
            <div style={{ animation: "fadeUp 0.3s ease" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 20 }}>
                <div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                    {SAMPLE_TRANSCRIPTS.map((s, i) => (
                      <button key={i} onClick={() => loadSample(s)} style={btnStyle()}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={transcript}
                    onChange={e => setTranscript(e.target.value)}
                    rows={8}
                    placeholder="Paste interview transcript here..."
                    style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }}
                  />
                  <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Duration (s)</label>
                      <input value={duration} onChange={e => setDuration(e.target.value)} placeholder="e.g. 45" style={{ ...inputStyle, marginTop: 4 }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Response Delay (s)</label>
                      <input value={delay} onChange={e => setDelay(e.target.value)} placeholder="e.g. 1.5" style={{ ...inputStyle, marginTop: 4 }} />
                    </div>
                  </div>
                  <button
                    onClick={handleAnalyze}
                    disabled={loading || !transcript.trim()}
                    style={{ ...btnStyle(true, loading || !transcript.trim()), marginTop: 12, width: "100%" }}
                  >
                    {loading ? "⟳ Analyzing..." : "▶ Run /analyze"}
                  </button>
                </div>

                {/* Request preview */}
                <div>
                  <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Request Preview</div>
                  <pre style={{
                    background: "#080810", border: `1px solid ${COLORS.border}`, borderRadius: 10,
                    padding: 16, fontSize: 11, color: "#8888b8", overflowX: "auto", height: 220, overflowY: "auto",
                  }}>
                    {JSON.stringify({
                      transcript: transcript.slice(0, 80) + (transcript.length > 80 ? "..." : ""),
                      ...(duration ? { duration: parseFloat(duration) || null } : {}),
                      ...(delay ? { response_delay: parseFloat(delay) || null } : {}),
                    }, null, 2)}
                  </pre>
                  <div style={{ marginTop: 12, fontSize: 11, color: COLORS.muted }}>
                    Endpoint: <span style={{ color: COLORS.accent }}>POST {API_BASE_URL}/analyze</span>
                  </div>
                </div>
              </div>

              <ResultPanel result={result} error={error} />
              <RawJSON data={result} />
            </div>
          )}

          {/* ── FILLERS TAB ── */}
          {activeTab === "fillers" && (
            <div style={{ animation: "fadeUp 0.3s ease" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 20 }}>
                <div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                    {SAMPLE_TRANSCRIPTS.map((s, i) => (
                      <button key={i} onClick={() => setTranscript(s.text)} style={btnStyle()}>{s.label}</button>
                    ))}
                  </div>
                  <textarea
                    value={transcript}
                    onChange={e => setTranscript(e.target.value)}
                    rows={8}
                    placeholder="Transcript to detect fillers..."
                    style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }}
                  />
                  <button
                    onClick={handleFillers}
                    disabled={loadingFiller || !transcript.trim()}
                    style={{ ...btnStyle(true, loadingFiller || !transcript.trim()), marginTop: 12, width: "100%" }}
                  >
                    {loadingFiller ? "⟳ Detecting..." : "▶ Run /fillers"}
                  </button>
                </div>
                <div>
                  {fillerError && (
                    <div style={{ background: "#2e0a0a", border: `1px solid #5c1515`, borderRadius: 10, padding: 16, color: COLORS.red, fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
                      ⚠ {fillerError}
                    </div>
                  )}
                  {fillerResult && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14, animation: "fadeUp 0.3s ease" }}>
                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        <MetaPill label="Words" value={fillerResult.word_count} />
                        <MetaPill label="Fillers" value={fillerResult.total_fillers} />
                        <MetaPill label="Ratio" value={`${(fillerResult.filler_ratio * 100).toFixed(1)}%`} />
                        <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 4 }}><Badge label={fillerResult.severity} /></div>
                      </div>
                      <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 16 }}>
                        <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>Top Fillers</div>
                        {fillerResult.top_fillers.length === 0
                          ? <div style={{ fontSize: 12, color: COLORS.muted }}>No fillers detected 🎉</div>
                          : fillerResult.top_fillers.map(f => (
                            <FillerBar key={f.word} word={f.word} count={f.count}
                              maxCount={Math.max(...fillerResult.top_fillers.map(x => x.count))} />
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <RawJSON data={fillerResult} />
            </div>
          )}

          {/* ── BATCH TAB ── */}
          {activeTab === "batch" && (
            <div style={{ animation: "fadeUp 0.3s ease" }}>
              <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Batch Payload (3 sample transcripts)</div>
                <pre style={{ fontSize: 11, color: "#8888b8", overflowX: "auto", maxHeight: 220 }}>
                  {JSON.stringify({ items: SAMPLE_TRANSCRIPTS.map(s => ({ transcript: s.text.slice(0, 50) + "...", duration: s.duration, response_delay: s.response_delay })) }, null, 2)}
                </pre>
              </div>
              <button
                onClick={handleBatch}
                disabled={loadingBatch}
                style={{ ...btnStyle(true, loadingBatch), marginBottom: 20 }}
              >
                {loadingBatch ? "⟳ Running Batch..." : "▶ Run /analyze/batch (3 items)"}
              </button>
              {batchError && (
                <div style={{ background: "#2e0a0a", border: `1px solid #5c1515`, borderRadius: 10, padding: 16, color: COLORS.red, fontSize: 12, marginBottom: 16 }}>
                  ⚠ {batchError}
                </div>
              )}
              {batchResult && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16, animation: "fadeUp 0.3s ease" }}>
                  <div style={{ fontSize: 12, color: COLORS.muted }}>{batchResult.count} results returned</div>
                  {batchResult.results.map((r, i) => (
                    <div key={i} style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 20 }}>
                      <div style={{
                        fontFamily: "'Syne', sans-serif", fontSize: 14, fontWeight: 700,
                        color: COLORS.text, marginBottom: 14,
                      }}>
                        [{i}] {SAMPLE_TRANSCRIPTS[i]?.label}
                      </div>
                      {r.error
                        ? <div style={{ color: COLORS.red, fontSize: 12 }}>⚠ {r.error}</div>
                        : (
                          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <Arc score={r.scores?.overall} size={56} />
                              <Badge label={r.grades?.overall} />
                            </div>
                            {["clarity", "professionalism", "confidence", "pacing", "response_time"].map(k => (
                              <div key={k} style={{ textAlign: "center" }}>
                                <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{k.replace("_", " ")}</div>
                                <Arc score={r.scores?.[k]} size={44} />
                              </div>
                            ))}
                            <MetaPill label="Fillers" value={r.filler_words?.total_count} />
                            <MetaPill label="WPM" value={r.meta?.wpm} />
                          </div>
                        )
                      }
                    </div>
                  ))}
                </div>
              )}
              <RawJSON data={batchResult} />
            </div>
          )}

          {/* ── HEALTH TAB ── */}
          {activeTab === "health" && (
            <div style={{ animation: "fadeUp 0.3s ease" }}>
              <button
                onClick={handleHealth}
                disabled={loadingHealth}
                style={{ ...btnStyle(true, loadingHealth), marginBottom: 20 }}
              >
                {loadingHealth ? "⟳ Checking..." : "▶ GET /health"}
              </button>
              {healthResult && (
                <div style={{ animation: "fadeUp 0.3s ease", display: "flex", flexDirection: "column", gap: 14 }}>
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 10,
                    background: healthResult.status === "ok" ? "#0d2e22" : "#2e0a0a",
                    border: `1px solid ${healthResult.status === "ok" ? COLORS.green + "60" : "#5c1515"}`,
                    borderRadius: 10, padding: "12px 20px",
                  }}>
                    <div style={{
                      width: 10, height: 10, borderRadius: "50%",
                      background: healthResult.status === "ok" ? COLORS.green : COLORS.red,
                      animation: "pulse 2s infinite",
                    }} />
                    <span style={{
                      fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 700,
                      color: healthResult.status === "ok" ? COLORS.green : COLORS.red,
                    }}>
                      {healthResult.status === "ok" ? "API is healthy" : "API error"}
                    </span>
                  </div>
                  {healthResult.ml && (
                    <div style={{ display: "flex", gap: 10 }}>
                      {Object.entries(healthResult.ml).map(([lib, active]) => (
                        <div key={lib} style={{
                          fontFamily: "'DM Mono', monospace", fontSize: 12, padding: "6px 16px",
                          borderRadius: 20, border: `1px solid ${active ? COLORS.green + "60" : COLORS.border}`,
                          color: active ? COLORS.green : COLORS.muted,
                          background: active ? "#0d2e2220" : "transparent",
                        }}>
                          {active ? "✓" : "✗"} {lib}
                        </div>
                      ))}
                    </div>
                  )}
                  <RawJSON data={healthResult} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
